import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Button,
  Box,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import StopIcon from '@material-ui/icons/Stop';
import ScheduleIcon from '@material-ui/icons/Schedule';
import { TektonPipeline, TektonPipelineRun, getPipelineRunStatus, getStatusColor, formatTime } from '../types';
import { usePipelineCardStyles } from './pipelineCardStyles';

export interface PipelineWithRuns {
  pipeline: TektonPipeline;
  lastRun?: TektonPipelineRun;
  recentRuns: TektonPipelineRun[];
}

export interface PipelineCardsProps {
  /** Pipeline 列表（带运行记录） */
  pipelinesWithRuns: PipelineWithRuns[];
  /** 正在执行的 Pipeline 名称集合 */
  executingPipelines?: Set<string>;
  /** 点击执行按钮 */
  onExecute: (pipeline: TektonPipeline) => void;
  /** 点击取消按钮 */
  onCancel?: (pipeline: TektonPipeline) => void;
  /** 是否显示取消按钮 */
  showCancelButton?: boolean;
  /** 是否有执行权限 */
  canExecute?: boolean;
  /** 权限检查是否完成 */
  ownerCheckDone?: boolean;
}

export const PipelineCards: React.FC<PipelineCardsProps> = ({
  pipelinesWithRuns,
  executingPipelines = new Set(),
  onExecute,
  onCancel,
  showCancelButton = true,
  canExecute = true,
  ownerCheckDone = true,
}) => {
  const classes = usePipelineCardStyles();

  // 检查是否有正在运行的 PipelineRun
  const hasRunningRun = (recentRuns: TektonPipelineRun[]): boolean => {
    return recentRuns.some(run => {
      const status = getPipelineRunStatus(run);
      return status === 'Running' || status === 'Pending';
    });
  };

  // 获取正在运行的 PipelineRun
  const getRunningRun = (recentRuns: TektonPipelineRun[]): TektonPipelineRun | undefined => {
    return recentRuns.find(run => {
      const status = getPipelineRunStatus(run);
      return status === 'Running' || status === 'Pending';
    });
  };

  return (
    <Grid container spacing={3}>
      {pipelinesWithRuns.map(({ pipeline, lastRun, recentRuns }) => {
        const pipelineName = pipeline.metadata.name;
        const isExecuting = executingPipelines.has(pipelineName) || hasRunningRun(recentRuns);
        const runningRun = getRunningRun(recentRuns);

        // 填充状态圆点到5个
        const statusDots = [];
        for (let i = 0; i < 5; i++) {
          if (i < recentRuns.length) {
            const run = recentRuns[i];
            const status = getPipelineRunStatus(run);
            statusDots.push(
              <Box
                key={run.metadata.name}
                className={classes.statusDot}
                style={{ backgroundColor: getStatusColor(status) }}
                title={`${run.metadata.name}: ${status}`}
              />
            );
          } else {
            statusDots.push(
              <Box
                key={`empty-${i}`}
                className={classes.statusDot}
                style={{ backgroundColor: '#e0e0e0' }}
                title="无记录"
              />
            );
          }
        }

        return (
          <Grid item xs={12} sm={6} md={4} key={pipeline.metadata.uid || pipelineName}>
            <Card className={classes.card}>
              <CardHeader
                className={classes.cardHeader}
                title={
                  <Typography className={classes.cardTitle} title={pipelineName}>
                    {pipelineName}
                  </Typography>
                }
                subheader={
                  <Box className={classes.statusDots} mt={0.5}>
                    {statusDots}
                  </Box>
                }
              />
              <CardContent className={classes.cardContent}>
                <Box className={classes.infoSection}>
                  <Box className={classes.infoLine}>
                    <Typography variant="caption" color="textSecondary">
                      参数: {pipeline.spec.params?.length || 0} 个
                    </Typography>
                  </Box>
                  <Box className={classes.infoLine}>
                    <ScheduleIcon style={{ fontSize: 14 }} />
                    <Typography variant="caption" color="textSecondary">
                      创建: {formatTime(pipeline.metadata.creationTimestamp)}
                    </Typography>
                  </Box>
                  {lastRun && (
                    <Box className={classes.infoLine}>
                      <ScheduleIcon style={{ fontSize: 14 }} />
                      <Typography variant="caption" color="textSecondary">
                        最近: {formatTime(lastRun.status?.startTime || lastRun.metadata.creationTimestamp)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* 操作按钮 */}
                <Box className={classes.actionButtons}>
                  {isExecuting ? (
                    <>
                      <Tooltip title={runningRun ? `正在运行: ${runningRun.metadata.name}` : '执行中'}>
                        <Box className={classes.runningStatus}>
                          <CircularProgress size={16} />
                          <Typography variant="caption">运行中</Typography>
                        </Box>
                      </Tooltip>
                      {showCancelButton && onCancel && (
                        <Button
                          variant="outlined"
                          size="small"
                          className={classes.cancelButton}
                          startIcon={<StopIcon />}
                          onClick={() => onCancel(pipeline)}
                        >
                          取消
                        </Button>
                      )}
                    </>
                  ) : (
                    <Tooltip title={canExecute ? "执行 Pipeline" : "无权限：您不是此组件的 Owner"}>
                      <span style={{ width: '100%' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          fullWidth
                          className={classes.executeButton}
                          startIcon={<PlayArrowIcon />}
                          onClick={() => onExecute(pipeline)}
                          disabled={!canExecute || !ownerCheckDone}
                        >
                          {!ownerCheckDone ? '检查中...' : (canExecute ? '执行' : '无权限')}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default PipelineCards;
